import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Container,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
} from "@mui/icons-material";
import CustomStepper from "../../../../custom-components/CustomStepper";
import { getStepperSteps, getStepIndex, getNextStep, getPreviousStep } from "../../../../../utils/stepperConfig";

const BaseOutputPage = ({ 
  currentStepName, 
  nextStepName, 
  children, 
  nextButtonText,
  showNextButton = true,
  nextButtonDisabled = false,
  onNext,
  onBack,
  additionalButtons = [],
  sx = {}
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    automationType, 
    preferences, 
    environment, 
    testCases,
    currentTestCaseIndex = 0,
    totalTestCases = 35,
    currentStep,
    currentStepIndex: stateStepIndex
  } = location.state || {};

  // Get dynamic steps based on framework selection
  const progressSteps = getStepperSteps(preferences);
  const currentStepIndex = stateStepIndex !== undefined ? stateStepIndex : getStepIndex(progressSteps, currentStepName);

  // Default handlers
  const defaultHandleBack = () => {
    const previousStep = getPreviousStep(progressSteps, currentStepIndex);
    if (previousStep) {
      const previousStepPath = getStepPath(previousStep);
      navigate(previousStepPath, {
        state: { 
          automationType, 
          preferences, 
          environment, 
          testCases,
          currentTestCaseIndex,
          totalTestCases,
          currentStep: previousStep,
          currentStepIndex: currentStepIndex - 1
        }
      });
    } else {
      // Go back to test cases page if no previous step
      navigate("/dashboard/test-automation/test-cases", {
        state: { automationType, preferences, environment, testCases }
      });
    }
  };

  const defaultHandleNext = () => {
    const nextStep = nextStepName || getNextStep(progressSteps, currentStepIndex);
    if (nextStep) {
      const nextStepPath = getStepPath(nextStep);
      navigate(nextStepPath, {
        state: { 
          automationType, 
          preferences, 
          environment, 
          testCases,
          currentTestCaseIndex,
          totalTestCases,
          currentStep: nextStep,
          currentStepIndex: currentStepIndex + 1
        }
      });
    }
  };

  // Helper function to get route path based on step name
  const getStepPath = (stepName) => {
    const stepPaths = {
      "Test Case Imported": "/dashboard/test-automation/playwright-typescript-mocha/ntnp/test-case-imported",
      "Introduce SUT": "/dashboard/test-automation/playwright-typescript-mocha/ntnp/introduce-sut",
      "Generate Locators": "/dashboard/test-automation/playwright-typescript-mocha/ntnp/generate-locators",
      "Generate Page Objects": "/dashboard/test-automation/playwright-typescript-mocha/ntnp/generate-page-objects",
      "Generate Test Files": "/dashboard/test-automation/playwright-typescript-mocha/ntnp/generate-test-files",
    };
    return stepPaths[stepName] || "/dashboard/test-automation/playwright-typescript-mocha/ntnp/test-case-imported";
  };

  const handleBack = onBack || defaultHandleBack;
  const handleNext = onNext || defaultHandleNext;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "#F6F3F2",
        display: "flex",
        flexDirection: "column",
        py: { xs: 2, sm: 3, md: 4 },
        px: { xs: 1, sm: 2 },
        ...sx,
      }}
    >
      <Container 
        maxWidth="xl" 
        sx={{ 
          width: "100%", 
          flex: 1,
          px: { xs: 1, sm: 2, md: 3 },
        }}
      >
        {/* Custom Stepper */}
        <Box sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
          <CustomStepper
            steps={progressSteps}
            activeStep={currentStepIndex}
            maxWidth="100%"
            containerWidth="100%"
            activeColor="#1976d2"
            completedColor="#40a9ff"
            pendingColor="#d1d1d1"
            showNextStep={true}
            showStepCount={true}
            title="Now"
            inProgressText="(In Progress)"
            completedText="(Completed)"
            nextText="Next"
            completedNextText="Completed"
            stepText="Step"
          />
        </Box>

        {/* Main Content */}
        <Card sx={{ 
          backgroundColor: "white",
          borderRadius: { xs: "4px", sm: "8px" },
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          mb: { xs: 2, sm: 3, md: 4 },
          overflow: "hidden",
        }}>
          <CardContent sx={{ 
            p: { xs: 2, sm: 3, md: 4 },
            "&:last-child": { pb: { xs: 2, sm: 3, md: 4 } }
          }}>
            {children}
          </CardContent>
        </Card>

        {/* Footer Navigation */}
        <Box sx={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 0 },
          backgroundColor: "transparent",
          borderRadius: "0 0 8px 8px",
          mt: { xs: 2, sm: 3, md: 4 },
        }}>
          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={handleBack}
            sx={{
              width: { xs: "100%", sm: 120 },
              height: { xs: 48, sm: 40 },
              border: '2px solid #0094FB',
              borderRadius: 2,
              backgroundColor: 'transparent',
              padding: { xs: '12px 16px', sm: '8px 16px' },
              fontFamily: 'Manrope',
              fontWeight: 700,
              fontSize: { xs: 18, sm: 20 },
              lineHeight: '24px',
              letterSpacing: 0.15,
              color: '#0094FB',
              textTransform: 'none',
              '&:hover': {
                border: '2px solid #0094FB',
                backgroundColor: 'rgba(0, 148, 251, 0.04)',
              },
              transition: 'all 0.3s ease-out'
            }}
          >
            Back
          </Button>

          <Box sx={{ 
            display: "flex", 
            gap: { xs: 1, sm: 2 }, 
            alignItems: "center",
            flexDirection: { xs: "column", sm: "row" },
            width: { xs: "100%", sm: "auto" },
          }}>
            {/* Additional Buttons */}
            {additionalButtons.map((button, index) => (
              <Button
                key={index}
                variant={button.variant || "outlined"}
                startIcon={button.startIcon}
                sx={{
                  width: { xs: "100%", sm: 120 },
                  height: { xs: 48, sm: 40 },
                  border: button.variant === "contained" ? 'none' : '2px solid #0094FB',
                  borderRadius: 2,
                  backgroundColor: button.variant === "contained" ? '#0094FB' : 'transparent',
                  padding: { xs: '12px 16px', sm: '8px 16px' },
                  fontFamily: 'Manrope',
                  fontWeight: 700,
                  fontSize: { xs: 18, sm: 20 },
                  lineHeight: '24px',
                  letterSpacing: 0.15,
                  color: button.variant === "contained" ? 'white' : '#0094FB',
                  textTransform: 'none',
                  '&:hover': {
                    border: button.variant === "contained" ? 'none' : '2px solid #0094FB',
                    backgroundColor: button.variant === "contained" ? '#007ACC' : 'rgba(0, 148, 251, 0.04)',
                  },
                  transition: 'all 0.3s ease-out'
                }}
                endIcon={button.endIcon}
                onClick={button.onClick}
                disabled={button.disabled}
              >
                {button.text}
              </Button>
            ))}

            {/* Next Button */}
            {showNextButton && (
              <Button
                variant="contained"
                endIcon={<NextIcon />}
                onClick={handleNext}
                sx={{
                  width: { xs: "100%", sm: 120 },
                  height: { xs: 48, sm: 40 },
                  backgroundColor: '#0094FB',
                  borderRadius: 2,
                  padding: { xs: '12px 16px', sm: '8px 16px' },
                  fontFamily: 'Manrope',
                  fontWeight: 700,
                  fontSize: { xs: 18, sm: 20 },
                  lineHeight: '24px',
                  letterSpacing: 0.15,
                  color: 'white',
                  textTransform: 'none',
                  border: 'none',
                  '&:hover': {
                    backgroundColor: '#007ACC',
                  },
                  transition: 'all 0.3s ease-out'
                }}
                disabled={nextButtonDisabled}
              >
                {nextButtonText || "Next"}
              </Button>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default BaseOutputPage;
